import { toMinorUnits, type MoneyString } from "@ibada/shared-types/finance"
import type {
  BranchFinanceBoardBreakdown,
  BranchFinanceBoardMarginMonth,
} from "@ibada/shared-types/overview"

import { BudgetLineType } from "@/features/budgeting/model/budget-enums"
import type { BudgetPerformanceLine } from "@/features/budgeting/model/budget-performance-types"
import { barRatio } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { DEFAULT_CURRENCY_LOCALE, currencyFormatOptions } from "@/shared/lib/formatting/currency"

/*
 * Presentation helpers for the F1 tiles. Money stays a string everywhere it is SHOWN; it becomes a number only for
 * bar geometry. No total, share or comparison is computed here: the server sends them, and these helpers only pick
 * which one a sentence states. Time (the budget's elapsed share) is the one quotient worked out in the browser.
 */

/** Basis points as a whole percent. */
export const bpToPercent = (bp: number) => Math.round(bp / 100)

export const isZeroMoney = (value: string) => Number(value) === 0

/** A breakdown with nothing recorded in its period. */
export const isEmptyBreakdown = (breakdown: BranchFinanceBoardBreakdown) =>
  isZeroMoney(breakdown.total) && breakdown.rows.length === 0

export interface BreakdownBar {
  id: string
  name: string
  amount: MoneyString
  /** Bar length (0 to 1) against the largest listed line. */
  ratio: number
}

/** The listed lines as bars on one shared scale; `nameOf` resolves seeded names in the reader's language. */
export function breakdownBars(
  breakdown: BranchFinanceBoardBreakdown,
): BreakdownBar[] {
  const max = Math.max(0, ...breakdown.rows.map((row) => Math.abs(Number(row.amount)) || 0))
  return breakdown.rows.map((row) => ({
    id: row.accountingAccountId,
    name: row.name,
    amount: row.amount,
    ratio: barRatio(row.amount, max),
  }))
}

/** The sentence under "where the money comes from": the top line's share, and how it compares with before. */
export enum TopSourceComparison {
  /** Same rounded share as the previous period. */
  Same = "same",
  /** A different share. */
  Changed = "changed",
  /** Nothing to compare with: the line or the whole previous period was empty. */
  None = "none",
}

export interface TopSource {
  name: string
  percent: number
  previousPercent: number | null
  comparison: TopSourceComparison
}

export function topSource(
  breakdown: BranchFinanceBoardBreakdown,
): TopSource | null {
  const top = breakdown.rows[0]
  if (!top) return null
  const percent = bpToPercent(top.shareBp)
  const comparable = !isZeroMoney(breakdown.previousTotal) && !isZeroMoney(top.previousAmount)
  const previousPercent = comparable ? bpToPercent(top.previousShareBp) : null
  return {
    name: top.name,
    percent,
    previousPercent,
    comparison:
      previousPercent === null
        ? TopSourceComparison.None
        : previousPercent === percent
          ? TopSourceComparison.Same
          : TopSourceComparison.Changed,
  }
}

/** The biggest move vs the previous period as a signed whole percent, or null (server-chosen). */
export function biggestChange(
  breakdown: BranchFinanceBoardBreakdown,
): { name: string; percent: number } | null {
  const change = breakdown.biggestChange
  if (!change) return null
  const percent = bpToPercent(change.changeBp)
  return percent === 0 ? null : { name: change.name, percent }
}

/** The kept-per-100 mini bars: heights against the best month, negative months drawn flat. */
export function keptBars(months: readonly BranchFinanceBoardMarginMonth[]) {
  const max = Math.max(1, ...months.map((month) => month.keptPer100 ?? 0))
  return months.map((month) => ({
    month: month.month,
    kept: month.keptPer100,
    ratio: month.keptPer100 !== null && month.keptPer100 > 0 ? month.keptPer100 / max : 0,
  }))
}

/* ----------------------------------- Budget ----------------------------------- */

/** How far through the budget's own period today is, as a whole percent (0 to 100). Time, not money. */
export function budgetPacePercent(reportingPeriod: { from: string; to: string }, now: Date): number | null {
  const from = new Date(reportingPeriod.from).getTime()
  const to = new Date(reportingPeriod.to).getTime()
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null
  return Math.round(Math.min(1, Math.max(0, (now.getTime() - from) / (to - from))) * 100)
}

/** A line runs ahead of pace when it has used this many points more of its plan than of its time. */
export const BUDGET_AHEAD_OF_PACE_POINTS = 5

export enum BudgetRowState {
  OnPace = "on-pace",
  /** An expense line using its plan faster than time passes. */
  AheadOfPace = "ahead",
  /** An expense line at or past its whole plan. */
  Over = "over",
}

export interface BudgetRow {
  id: string
  label: string
  planned: MoneyString
  actual: MoneyString
  utilizationPct: number | null
  state: BudgetRowState
}

/** How many budget lines the tile draws. */
export const BUDGET_TILE_ROWS = 4

/**
 * The lines of one direction (expenses or income), largest plan first. Utilization is the server's; only the
 * expense side can run ahead of pace, since income arriving early is good news.
 */
export function budgetRows(
  lines: readonly BudgetPerformanceLine[],
  type: BudgetLineType,
  pacePercent: number | null,
  currency: string,
): BudgetRow[] {
  // Exact ordering on minor units: two plans a cent apart must not compare equal as floats.
  const minor = (value: MoneyString) => toMinorUnits(value, currency)
  return lines
    .filter((line) => line.type === type)
    .sort((left, right) => {
      const a = minor(left.planned)
      const b = minor(right.planned)
      return a === b ? 0 : a > b ? -1 : 1
    })
    .slice(0, BUDGET_TILE_ROWS)
    .map((line) => {
      const used = line.utilizationPct
      const state =
        type !== BudgetLineType.Expense || used === null
          ? BudgetRowState.OnPace
          : used >= 100
            ? BudgetRowState.Over
            : pacePercent !== null && used > pacePercent + BUDGET_AHEAD_OF_PACE_POINTS
              ? BudgetRowState.AheadOfPace
              : BudgetRowState.OnPace
      return {
        id: line.budgetLineId,
        label: line.accountName ?? line.lineTitle,
        planned: line.planned,
        actual: line.actual,
        utilizationPct: used,
        state,
      }
    })
}

/* ---------------------------------- Control ----------------------------------- */

export enum ControlVerdict {
  Ready = "ready",
  AlmostReady = "almost",
  /** The ledger does not balance: nothing else matters until it does. */
  NotReady = "not-ready",
}

/** The close verdict from the checks the system can prove, and how many points remain. */
export function controlVerdict(checks: { balanced: boolean; reviewable: number; missingInPeriod: number; toReconcile: number }) {
  const points =
    Number(!checks.balanced) + Number(checks.reviewable > 0) + Number(checks.missingInPeriod > 0) + Number(checks.toReconcile > 0)
  const verdict = !checks.balanced ? ControlVerdict.NotReady : points === 0 ? ControlVerdict.Ready : ControlVerdict.AlmostReady
  return { verdict, points }
}

/** A whole amount ("100 $") for the margin sentence's hypothetical units: never a recorded figure. */
export function formatWholeAmount(value: number, currency: string, locale: string = DEFAULT_CURRENCY_LOCALE): string {
  return new Intl.NumberFormat(locale, {
    ...currencyFormatOptions(currency),
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/* ---------------------------------- Wording ----------------------------------- */

/** What a comparison sentence compares with, so it can name it. */
export enum ComparisonBasis {
  /** The whole previous month ("par rapport à octobre"). */
  Month = "month",
  /** The same days of the previous month ("à la même date en octobre"). */
  MonthToDate = "monthToDate",
  /** Any other range: the period of equal length just before. */
  Period = "period",
}

/**
 * How to name the period a shown period is compared with. `from`/`to` are local `YYYY-MM-DD` days. A period inside
 * one calendar month starting on the 1st compares with the month before: whole when it ends on the month's last
 * day, otherwise to the same date (the server's previous comparable period).
 */
export function comparisonBasis(from: string, to: string): { basis: ComparisonBasis; month: string | null } {
  const [fy, fm, fd] = from.split("-").map(Number)
  const [ty, tm, td] = to.split("-").map(Number)
  if (!fy || !fm || !fd || !ty || !tm || !td || fd !== 1 || fy !== ty || fm !== tm) {
    return { basis: ComparisonBasis.Period, month: null }
  }
  const previous = new Date(Date.UTC(fy, fm - 2, 15))
  const month = `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, "0")}`
  const lastDay = new Date(Date.UTC(ty, tm, 0)).getUTCDate()
  return { basis: td >= lastDay ? ComparisonBasis.Month : ComparisonBasis.MonthToDate, month }
}

/** A phrase capitalised for the start of a line ("Ce mois"). */
export const capitalize = (value: string, locale: string) => value.charAt(0).toLocaleUpperCase(locale) + value.slice(1)

/** The local calendar day before `now`, as `YYYY-MM-DD` (a pledge due by then is late). */
export function localDayBefore(now: Date): string {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`
}
